import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState({ count: 0, lastSubmission: null, cooldownEnd: null });

  useEffect(() => {
    document.title = "Contact | Musa Yusuf Magoban";
    window.scrollTo(0, 0);
    
    // Initialize EmailJS with the provided public key
    emailjs.init("Hs1BJM_D7tpRB-hrF");
    
    // Load saved form data from localStorage
    const savedFormData = localStorage.getItem('contactFormData');
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(parsedData);
        setIsFormDirty(true);
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }

    // Load rate limit info from localStorage
    const savedRateLimitInfo = localStorage.getItem('contactFormRateLimit');
    if (savedRateLimitInfo) {
      try {
        const parsedRateLimit = JSON.parse(savedRateLimitInfo);
        setRateLimitInfo(parsedRateLimit);
      } catch (error) {
        console.error('Error loading rate limit info:', error);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (isFormDirty) {
      localStorage.setItem('contactFormData', JSON.stringify(formData));
    }
  }, [formData, isFormDirty]);

  const validateField = (name, value) => {
    const errors = {};
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Full name is required';
        } else if (value.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
          errors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      
      case 'email':
        if (!value.trim()) {
          errors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      
      case 'subject':
        if (!value.trim()) {
          errors.subject = 'Subject is required';
        } else if (value.trim().length < 5) {
          errors.subject = 'Subject must be at least 5 characters';
        } else if (value.trim().length > 100) {
          errors.subject = 'Subject must be less than 100 characters';
        }
        break;
      
      case 'message':
        if (!value.trim()) {
          errors.message = 'Message is required';
        } else if (value.trim().length < 10) {
          errors.message = 'Message must be at least 10 characters';
        } else if (value.trim().length > 2000) {
          errors.message = 'Message must be less than 2000 characters';
        }
        break;
      
      default:
        break;
    }
    
    return errors;
  };

  const validateForm = () => {
    const errors = {};
    
    Object.keys(formData).forEach(field => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(errors, fieldErrors);
    });
    
    return errors;
  };

  const checkRateLimit = () => {
    const now = new Date().getTime();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    const maxSubmissions = 3;
    const cooldownPeriod = 15 * 60 * 1000; // 15 minutes cooldown

    // Check if we're in a cooldown period
    if (rateLimitInfo.cooldownEnd && now < rateLimitInfo.cooldownEnd) {
      const remainingTime = Math.ceil((rateLimitInfo.cooldownEnd - now) / 60000);
      return {
        allowed: false,
        reason: 'cooldown',
        remainingTime
      };
    }

    // Reset count if more than an hour has passed since last submission
    if (rateLimitInfo.lastSubmission && (now - rateLimitInfo.lastSubmission) > oneHour) {
      return {
        allowed: true,
        newCount: 1,
        newLastSubmission: now,
        newCooldownEnd: null
      };
    }

    // Check if we've exceeded the rate limit
    if (rateLimitInfo.count >= maxSubmissions) {
      const cooldownEnd = now + cooldownPeriod;
      const newRateLimit = {
        count: rateLimitInfo.count,
        lastSubmission: rateLimitInfo.lastSubmission,
        cooldownEnd
      };
      
      // Save updated rate limit info
      localStorage.setItem('contactFormRateLimit', JSON.stringify(newRateLimit));
      setRateLimitInfo(newRateLimit);

      return {
        allowed: false,
        reason: 'limit_exceeded',
        remainingTime: Math.ceil(cooldownPeriod / 60000)
      };
    }

    // Allow submission and increment count
    return {
      allowed: true,
      newCount: rateLimitInfo.count + 1,
      newLastSubmission: now,
      newCooldownEnd: null
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setIsFormDirty(true);
    
    // Real-time validation
    const fieldErrors = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: fieldErrors[name] || undefined
    }));
    
    // Clear submit status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Check rate limit
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      if (rateLimitCheck.reason === 'cooldown') {
        setSubmitStatus('rate_limit_cooldown');
        setFormErrors({ submit: `Please wait ${rateLimitCheck.remainingTime} minutes before sending another message.` });
      } else if (rateLimitCheck.reason === 'limit_exceeded') {
        setSubmitStatus('rate_limit_exceeded');
        setFormErrors({ submit: `Too many submissions. Please wait ${rateLimitCheck.remainingTime} minutes before trying again.` });
      }
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS template parameters
      const templateParams = {
        to_name: 'Musa Yusuf Magoban',
        from_name: formData.name.trim(),
        from_email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        reply_to: formData.email.trim(),
      };

      // Send email using EmailJS with the provided credentials
      await emailjs.send(
        'service_o794l14',    // Service ID
        'template_uf540ym',   // Template ID
        templateParams
      );

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFormErrors({});
      setIsFormDirty(false);
      
      // Update rate limit info
      const newRateLimit = {
        count: rateLimitCheck.newCount,
        lastSubmission: rateLimitCheck.newLastSubmission,
        cooldownEnd: rateLimitCheck.newCooldownEnd
      };
      setRateLimitInfo(newRateLimit);
      localStorage.setItem('contactFormRateLimit', JSON.stringify(newRateLimit));
      
      // Clear saved form data from localStorage
      localStorage.removeItem('contactFormData');
      
    } catch (error) {
      console.error('Email send error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    if (isFormDirty && (formData.name || formData.email || formData.subject || formData.message)) {
      if (window.confirm('Are you sure you want to clear the form? All unsaved data will be lost.')) {
        setFormData({ name: '', email: '', subject: '', message: '' });
        setFormErrors({});
        setIsFormDirty(false);
        localStorage.removeItem('contactFormData');
      }
    } else {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFormErrors({});
      setIsFormDirty(false);
      localStorage.removeItem('contactFormData');
    }
  };

  const contactMethods = [
    {
      method: "Email",
      value: "musayusufmagoban@gmail.com",
      icon: "Mail",
      link: "mailto:musayusufmagoban@gmail.com"
    },
    {
      method: "LinkedIn",
      value: "/in/musayusufmagoban",
      icon: "Linkedin",
      link: "https://linkedin.com/in/musayusufmagoban"
    },
    {
      method: "GitHub",
      value: "/Magoban",
      icon: "Github",
      link: "https://github.com/Magoban"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact | Musa Yusuf Magoban</title>
        <meta name="description" content="Get in touch with Musa Yusuf Magoban for cybersecurity opportunities, collaboration, or consultation." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Header Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Get In Touch
              </h1>
              <p className="text-base sm:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
                I'm always open to discussing cybersecurity opportunities, collaboration, 
                and sharing knowledge with the security community.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Contact Form */}
              <div className="bg-surface rounded-lg p-6 sm:p-8 security-shadow">
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Send a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      Full Name
                    </label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      error={formErrors.name}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      error={formErrors.email}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                      Subject
                    </label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Message subject"
                      error={formErrors.subject}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Your message..."
                      className={`w-full px-3 py-2 border rounded-lg bg-input text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                        formErrors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-border'
                      }`}
                    />
                    {formErrors.message && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <Icon name="AlertCircle" size={14} className="mr-1" />
                        {formErrors.message}
                      </p>
                    )}
                    <div className="mt-1 text-xs text-text-secondary">
                      {formData.message.length}/2000 characters
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <Button 
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={handleReset}
                      disabled={!isFormDirty}
                      className="w-full sm:w-auto"
                    >
                      Reset Form
                    </Button>
                    <Button 
                      type="submit"
                      variant="default"
                      size="lg"
                      iconName={isSubmitting ? "Loader2" : "Send"}
                      iconPosition="left"
                      className="cyber-gradient w-full sm:w-auto"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                  
                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="flex items-center p-4 bg-accent/10 border border-accent/20 rounded-lg">
                      <Icon name="CheckCircle" size={20} className="text-accent mr-3" />
                      <p className="text-accent text-sm">
                        Message sent successfully! I'll get back to you within 24-48 hours.
                      </p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="flex items-center p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <Icon name="AlertCircle" size={20} className="text-red-500 mr-3" />
                      <p className="text-red-500 text-sm">
                        Failed to send message. Please try again or contact me directly at 
                        <a href="mailto:musayusufmagoban@gmail.com" className="underline ml-1">
                          musayusufmagoban@gmail.com
                        </a>
                      </p>
                    </div>
                  )}
                  
                  {(submitStatus === 'rate_limit_exceeded' || submitStatus === 'rate_limit_cooldown') && (
                    <div className="flex items-center p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                      <Icon name="Clock" size={20} className="text-orange-500 mr-3" />
                      <div className="text-orange-500 text-sm">
                        <p className="font-medium mb-1">Rate limit reached</p>
                        <p>{formErrors.submit}</p>
                        <p className="mt-2 text-xs">
                          Rate limit: 3 messages per hour to prevent spam and ensure quality responses.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Rate Limit Info */}
                  {rateLimitInfo.count > 0 && rateLimitInfo.count < 3 && (
                    <div className="text-xs text-text-secondary text-center">
                      Messages sent: {rateLimitInfo.count}/3 per hour
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-6 lg:space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    Contact Information
                  </h2>
                  
                  <div className="space-y-4">
                    {contactMethods.map((contact, index) => (
                      <div key={index} className="flex items-center p-4 bg-surface rounded-lg security-shadow hover:security-shadow-strong transition-all">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                          <Icon name={contact.icon} size={20} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {contact.method}
                          </p>
                          {contact.link ? (
                            <a 
                              href={contact.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-secondary hover:text-secondary/80 transition-colors text-sm break-words"
                            >
                              {contact.value}
                            </a>
                          ) : (
                            <p className="text-text-secondary text-sm break-words">{contact.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-surface rounded-lg p-6 security-shadow">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Open to Opportunities
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Icon name="Briefcase" size={16} className="text-accent mr-3 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">SOC Analyst Positions</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="Users" size={16} className="text-accent mr-3 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">Cybersecurity Collaboration</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="BookOpen" size={16} className="text-accent mr-3 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">Knowledge Sharing</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="Coffee" size={16} className="text-accent mr-3 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">Networking & Mentorship</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Response Time
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    I typically respond to messages within 24-48 hours. 
                    For urgent matters, please mention it in your subject line.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Contact; 